import './pPartRefBadge.scss';

import { Badge } from '@cloudscape-design/components';
import { PartTypes, PPartStatus } from '@parallel-fluidics/shared/constants';
import { getPPartLabel } from '@parallel-fluidics/shared/models/projects/parts';
import PPartStatusBadge from 'components/Projects/pPartStatusBadge';
import useGetProjectPartByDocId from 'features/pPart/hooks/useGetProjectPartByDocId';
import { useGetProjectByDocId } from 'hooks/use-basicQueries';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * A part reference as a badge: "P068-0068-A-COC (Pipette interface -
 * Product)", with an optional "16× " quantity prefix for BOM lines. The
 * part resolves from the doc IDs through the shared cached query;
 * unresolved refs degrade to the doc ID. The part and its project both
 * resolve from the doc IDs through the shared cached queries — a BOM
 * line's project differs from the page's, so the badge looks up its own.
 * Green when quantified (BOM), grey for tools, yellow for pistons, blue
 * otherwise. A non-Active part shows its status badge inside this one, so
 * the status can never wrap onto a line where it reads as belonging to a
 * neighbouring badge.
 */
export default function PPartRefBadge({
  projectDocId,
  pPartDocId,
  quantity = null,
}) {
  const { data: part, isError } = useGetProjectPartByDocId({ projectDocId, pPartDocId });
  const { data: project } = useGetProjectByDocId(projectDocId);

  // Nothing while the part loads (no doc-ID flash); a visibly broken badge
  // when the ref doesn't resolve — hiding it would hide a real data problem.
  if (!part && !isError) return null;

  const text = part ? getPPartLabel(part, project?.name) : 'Unknown part';
  const showStatus = part && part.status !== PPartStatus.ACTIVE;

  let color = 'blue';
  if (isError) color = 'red';
  else if (quantity != null) color = 'green';
  else if (part?.type === PartTypes.TOOL) color = 'grey';
  else if (part?.type === PartTypes.PISTON) color = 'severity-low';

  return (
    <Badge color={color}>
      {quantity != null ? `${quantity}× ${text}` : text}
      {showStatus && (
        <span className="pPartRefBadge-status">
          <PPartStatusBadge status={part.status} />
        </span>
      )}
    </Badge>
  );
}

PPartRefBadge.propTypes = {
  projectDocId: PropTypes.string.isRequired,
  pPartDocId: PropTypes.string.isRequired,
  quantity: PropTypes.number,
};
